import React from "react";
import { twMerge } from "tailwind-merge";

/**
 * KpiGroup Component — Layout container for multiple KPIs
 *
 * Provides consistent grid layout for KPI cards with responsive columns
 * and spacing. Perfect for dashboard metric rows and summary sections.
 *
 * Usage:
 * ```tsx
 * <KpiGroup cols={{ base: 1, md: 2, lg: 4 }}>
 *   <Kpi label="Revenue" value={45231} />
 *   <Kpi label="Users" value={1234} />
 *   <Kpi label="Conversion" value={0.089} unit="%" />
 *   <Kpi label="Avg Order" value={89.50} unit="$" />
 * </KpiGroup>
 * ```
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type KpiGroupGap = "xs" | "sm" | "md" | "lg" | "xl";
export type KpiGroupAlign = "start" | "center" | "stretch";

export interface KpiGroupCols {
  base?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  "2xl"?: number;
}

export interface KpiGroupProps {
  /** KPI components to display */
  children: React.ReactNode;
  /** Responsive column configuration */
  cols?: KpiGroupCols | number;
  /** Gap between items using spacing tokens */
  gap?: KpiGroupGap;
  /** Vertical alignment of items */
  align?: KpiGroupAlign;
  /** Force equal heights for all items */
  equalizeHeights?: boolean;
  /** Optional header content */
  header?: React.ReactNode;
  /** Loading state for all children */
  isLoading?: boolean;
  /** Enable horizontal scrolling on mobile instead of stacking */
  scrollOnMobile?: boolean;
  /** Minimum width for each KPI item when scrollOnMobile is enabled */
  scrollItemMinWidth?: string;
  /** Additional CSS classes */
  className?: string;
}

// ——————————————————————————————————————————
// Helpers
// ——————————————————————————————————————————

const gapMap: Record<KpiGroupGap, string> = {
  xs: "gap-2",
  sm: "gap-3",
  md: "gap-4",
  lg: "gap-6",
  xl: "gap-8",
};

const alignMap: Record<KpiGroupAlign, string> = {
  start: "items-start",
  center: "items-center",
  stretch: "items-stretch",
};

function getColClasses(cols: KpiGroupCols | number): string {
  // Simple number - apply to all breakpoints
  if (typeof cols === "number") {
    return `grid-cols-${cols}`;
  }

  // Responsive object
  const classes: string[] = [];

  if (cols.base !== undefined) {
    classes.push(`grid-cols-${cols.base}`);
  }

  if (cols.sm !== undefined) {
    classes.push(`sm:grid-cols-${cols.sm}`);
  }

  if (cols.md !== undefined) {
    classes.push(`md:grid-cols-${cols.md}`);
  }

  if (cols.lg !== undefined) {
    classes.push(`lg:grid-cols-${cols.lg}`);
  }

  if (cols.xl !== undefined) {
    classes.push(`xl:grid-cols-${cols.xl}`);
  }

  if (cols["2xl"] !== undefined) {
    classes.push(`2xl:grid-cols-${cols["2xl"]}`);
  }

  return classes.join(" ");
}

// ——————————————————————————————————————————
// Main Component
// ——————————————————————————————————————————

export function KpiGroup({
  children,
  cols = { base: 1, sm: 2, md: 3, lg: 4 },
  gap = "md",
  align = "stretch",
  equalizeHeights = true,
  header,
  isLoading,
  scrollOnMobile = false,
  scrollItemMinWidth = "140px",
  className,
}: KpiGroupProps) {
  // Apply loading state to children if specified
  const enhancedChildren = isLoading
    ? React.Children.map(children, (child) => {
        if (
          React.isValidElement(child) &&
          child.type &&
          typeof child.type !== "string" &&
          "displayName" in child.type
        ) {
          return React.cloneElement(child as React.ReactElement<{ isLoading?: boolean }>, {
            isLoading: true,
          });
        }
        return child;
      })
    : children;

  // When scrollOnMobile is enabled, wrap each child in a flex item with min-width
  const scrollableChildren = scrollOnMobile
    ? React.Children.map(enhancedChildren, (child) => (
        <div className="shrink-0" style={{ minWidth: scrollItemMinWidth }}>
          {child}
        </div>
      ))
    : enhancedChildren;

  // Build responsive classes for scrollOnMobile mode
  // On mobile (base): horizontal flex with overflow scroll
  // On sm and up: switch to grid layout
  const scrollOnMobileClasses = scrollOnMobile
    ? twMerge(
        // Mobile: horizontal scroll
        "flex overflow-x-auto pb-2 -mb-2",
        gapMap[gap],
        // sm and up: switch to grid
        "sm:grid sm:overflow-visible sm:pb-0 sm:-mb-0",
        getColClasses({
          ...((typeof cols === "object" ? cols : { base: cols }) as KpiGroupCols),
          base: undefined,
        }),
        alignMap[align],
        equalizeHeights && "sm:auto-rows-fr"
      )
    : twMerge("grid", getColClasses(cols), gapMap[gap], alignMap[align], equalizeHeights && "auto-rows-fr", "w-full");

  return (
    <section className={twMerge("w-full", className)} aria-label="Key metrics">
      {header && <div className="mb-4">{header}</div>}

      <div className={scrollOnMobileClasses}>{scrollOnMobile ? scrollableChildren : enhancedChildren}</div>
    </section>
  );
}

// ——————————————————————————————————————————
// Compound API
// ——————————————————————————————————————————

KpiGroup.displayName = "KpiGroup";
